import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import HeaderFooterLayout from '../components/header-footer-layout';
import Header from '../components/header';
import ErrorMessage from '../components/error-message';
import { scrollToTop } from '../utils/helpers';
import { createEmptyPersonalInfo, type ExistingRepairBooking } from '../models/repair-bookings';
import BookingEditSelect from '../pages/booking-edit-select';
import BookingCancelDialog from '../pages/booking-cancel-dialog';
import DateSelection from '../pages/date-selection';
import TimeSelection from '../pages/time-selection';
import FinalConfirmation from '../pages/final-confirmation';
import Completion from '../pages/completion';
import InvalidNavigation from '../pages/invalid-navigation';
import useBookingEditPages from '../hooks/use-booking-edit-pages';
import useBookingState from '../hooks/use-booking-state';
import ShopSelection from '../pages/shop-selection';
import useBooking from '../hooks/use-booking';
import LoadingFallback from '../components/loading-fallback';
import { updateBooking, fetchTimeSlots, cancelBooking } from '../utils/api';
import { goToBookingTop } from '../utils/paths';
import useServiceAvailability from '../hooks/use-service-availability.ts';

const BookingEditCancelFlow = () => {
    const params = useParams<{ token: string }>();
    const csrfToken = window.preloadedState.environment.csrfToken;
    const bookingToken = params.token ?? '';
    const { confirmationId, shop, slot, personalInfo, error, loading } = useBooking(
        window.preloadedState.environment.csrfToken,
        bookingToken
    );
    const { booking, actions } = useBookingState<ExistingRepairBooking>(() => ({
        type: 'existing',
        shop: shop,
        day: '',
        year: 0,
        selectedSlot: slot,
        personalInfo: personalInfo ?? createEmptyPersonalInfo(),
        bookingToken: bookingToken,
        confirmationId: confirmationId,
    }));
    const { available, messages } = useServiceAvailability();

    const {
        currentPage,
        pageConfig,
        pushPageStack,
        popPageStack,
        complete,
        resetPageStack,
        session,
    } = useBookingEditPages(bookingToken, 'web', csrfToken);

    useEffect(() => {
        if (loading || !shop || !slot || !confirmationId || !personalInfo) {
            return;
        }
        actions.patchBooking({
            shop,
            selectedSlot: slot,
            personalInfo,
            confirmationId,
        });
    }, [actions, confirmationId, loading, personalInfo, shop, slot]);

    useEffect(() => {
        scrollToTop();
    }, [currentPage]);

    const resetFlow = () => {
        actions.reset();
        resetPageStack();
    };

    const redirectToBookingTop = () => {
        actions.reset();
        goToBookingTop();
    };

    return (
        <HeaderFooterLayout title={pageConfig.title} onHome={resetFlow}>
            {error && <ErrorMessage message={error.message} />}
            {!error && (
                <>
                    {pageConfig.showShopHeader && <Header title={booking.shop?.name ?? ''} />}
                    {currentPage.status === 'invalid' ? (
                        <InvalidNavigation onClose={resetFlow} />
                    ) : currentPage.status === 'select' || currentPage.status === 'cancel' ? (
                        <BookingEditSelect
                            booking={booking}
                            available={available || false}
                            messages={messages}
                            onSelectEdit={() => {
                                pushPageStack({ status: 'shop-selection' });
                            }}
                            onSelectCancel={() => pushPageStack({ status: 'cancel' })}
                        />
                    ) : currentPage.status === 'cancel-complete' ? (
                        <Completion type="cancel" onClose={redirectToBookingTop} />
                    ) : !session ? (
                        <LoadingFallback />
                    ) : currentPage.status === 'shop-selection' ? (
                        <ShopSelection
                            session={session}
                            selectedShop={booking.shop}
                            onComplete={(newShop) => {
                                if (newShop) {
                                    actions.setShop(newShop);
                                }
                                pushPageStack({ status: 'date-selection' });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'date-selection' ? (
                        <DateSelection
                            selectedSlot={booking.selectedSlot}
                            fetchTimeSlots={() =>
                                fetchTimeSlots(
                                    session.csrfToken,
                                    booking.shop!.shop_id,
                                    session.sessionId,
                                    booking.bookingToken
                                )
                            }
                            onComplete={(date, slots) => {
                                pushPageStack({
                                    status: 'time-selection',
                                    selectedDate: date,
                                    slots: slots,
                                });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'time-selection' ? (
                        <TimeSelection
                            selectedDate={currentPage.selectedDate}
                            slots={currentPage.slots}
                            onComplete={(slot) => {
                                actions.setSelectedSlot(slot);
                                pushPageStack({ status: 'confirm' });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'confirm' ? (
                        <FinalConfirmation
                            personalInfoConfig={{ granularity: 'standard' }}
                            booking={booking}
                            onSubmit={async () => {
                                const result = await updateBooking(
                                    session.csrfToken,
                                    session.sessionId,
                                    booking
                                );
                                complete({
                                    status: 'edit-complete',
                                    confirmationId: result.confirmation_id,
                                });
                            }}
                            onCancel={popPageStack}
                        />
                    ) : currentPage.status === 'edit-complete' ? (
                        <Completion
                            type="edit"
                            confirmationId={currentPage.confirmationId}
                            onClose={resetFlow}
                        />
                    ) : null}
                    {currentPage.status === 'cancel' && (
                        <BookingCancelDialog
                            confirmationId={booking.confirmationId}
                            startAt={booking.selectedSlot?.start_at ?? ''}
                            shopName={booking.shop?.name ?? ''}
                            onClose={popPageStack}
                            onExecute={async () => {
                                await cancelBooking(csrfToken, bookingToken);
                                complete({ status: 'cancel-complete' });
                            }}
                        />
                    )}
                </>
            )}
        </HeaderFooterLayout>
    );
};

export default BookingEditCancelFlow;
